import moment from "moment";
import React from "react";
import {
	Badge,
	Button,
	Dropdown,
	OverlayTrigger,
	Tooltip
} from "react-bootstrap";
import { CircularProgressbar, buildStyles } from "react-circular-progressbar";
import { withTranslation } from "react-i18next";
import { AiOutlineSetting, AiOutlineWarning } from "react-icons/ai";
import { BiPencil } from "react-icons/bi";
import { BsCircle, BsFillCircleFill, BsFillSquareFill } from "react-icons/bs";
import { MdLoop } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { FormFrequency, FormType, LS_COMPANY_INFO_KEY, LS_DCE_SUBMISSION_FILTER_KEY } from "../../../../core/constants/common";
import Utils from "../../../../core/utilities/Utils";
import "../Forms.scss";
import "../Materiality/MaterialityAssessment.scss";
import { Doughnut } from "react-chartjs-2";
import StackedCircularProgressBar from "../Materiality/StackedProgressBar";
import useHasPermission from "../../../../core/hooks/useHasPermission";

function AssessmentListCard({
	t, form, selfAssessment = true, formType = FormType.GENERAL, viewMode = 'LIST_CARD',
	onPermBubbleClick, onShareClick, onReshareClick, onQuickRemindClick, onEditAssessment, onExpiryEdit, onDuplicate, onReminderSetup,
	onShowQuestionPermissions
}) {
	const { hasDataCollectionUpdatePermission, hasDataCollectionViewPermission } = useHasPermission();
	const navigate = useNavigate();

	const notSharedCount = form.submissionStatusCount?.NOT_SHARED ?? 0;
	const inProgressCount = (form.submissionStatusCount?.IN_PROGRESS ?? 0)
		+ (form.submissionStatusCount?.CORRECTION_PENDING ?? 0)
		+ (form.submissionStatusCount?.PENDING_APPROVAL ?? 0);
	const draftCount = form.submissionStatusCount?.DRAFT ?? 0;
	const completedCount = form.submissionStatusCount?.COMPLETED ?? 0;
	const expiredCount = form.submissionStatusCount?.EXPIRED ?? 0;
	const totalFieldsCount = completedCount
		+ draftCount
		+ inProgressCount
		+ expiredCount;
	let percentage;
	const isCompleted = form.sharedOn
		&& inProgressCount === 0
		&& draftCount == 0
		&& completedCount > 0;
	// Append T23:59:59 so date-only strings (e.g. "2026-10-13") are parsed as end-of-day
	// in local time instead of UTC midnight — prevents early expiry for users ahead of UTC.
	const lastSubmissionDate = new Date((form.lastSubmissionDate || "").replace(/T.*/, "") + "T23:59:59");
	const isExpired = isCompleted === true ? false : lastSubmissionDate < new Date();
	if (!form.sharedOn) {
		percentage = 0;
	} else {
		percentage = totalFieldsCount === 0 ? 0 : (Math.round((100 / totalFieldsCount) * completedCount));
	}
	const showInProgressBadge = (form.sharedOn && !isExpired && (inProgressCount + draftCount) > 0);
	const showCompletedBadge = form.sharedOn
		&& ((inProgressCount + draftCount) == 0)
		&& completedCount > 0;
	const showNotStartedBadge = !form.sharedOn && (inProgressCount + completedCount + draftCount) == 0 && notSharedCount > 0;

	const renderPermissionBubbles = () => {
		return formType === FormType.SURVEY
			? renderPermissionBubblesForSurveyForm()
			: renderPermissionBubblesForGeneralForm();
	};


	const renderPermissionBubblesForSurveyForm = () => {
		let permCount = 0;
		const elements = [];
		const permittedUsers = [];
		form.formPermissions?.forEach((perm, pIndex) => {
			perm?.stakeholders?.forEach((user, uIndex) => {
				if (permittedUsers?.findIndex(u => u.id === user.id) >= 0) return;

				if (permCount >= 4) return;

				permittedUsers.push(user);
				elements.push(
					<li key={`assmtSharedGroup-${pIndex}-${uIndex}`} onClick={hasDataCollectionUpdatePermission() ? () => onPermBubbleClick() : null}>
						{Utils.getTextBasedProfilePic(user.name)}
					</li>
				);
				permCount++;
			});
		});
		if (permCount > 3) {
			elements.push(<li key='shareCountKey' className="Share_count" onClick={hasDataCollectionUpdatePermission() ? () => onPermBubbleClick() : null}>+{permCount - 3}</li>);
		}
		return elements;
	};

	const renderPermissionBubblesForGeneralForm = () => {
		let permCount = 0;
		const elements = [];
		form.formPermissions?.map((perm, pIndex) => {
			perm?.groups?.map((group, gIndex) => {
				if (permCount >= 3) {
				} else {
					elements.push(
						<li key={`assmtSharedGroup-${pIndex}-${gIndex}`} onClick={hasDataCollectionUpdatePermission() ? () => onPermBubbleClick() : null}>
							{Utils.getTextBasedProfilePic(group.name)}
						</li>
					);
				}
				permCount++;
			});
		});
		if (permCount > 3) {
			elements.push(<li key='shareCountKey' className="Share_count" onClick={hasDataCollectionUpdatePermission() ? () => onPermBubbleClick() : null}>+{permCount - 3}</li>);
		}
		return elements;
	};

	const FREQUENCY_BADGE_STYLES = {
		MONTHLY: { backgroundColor: '#3b82f6', color: '#ffffff' },
		QUARTERLY: { backgroundColor: '#10b981', color: '#ffffff' },
		HALF_YEARLY: { backgroundColor: '#f59e0b', color: '#ffffff' },
		ANNUALLY: { backgroundColor: '#8b5cf6', color: '#ffffff' },
	};

	const getCompanyDataFrequencyType = () => {
		try {
			const info = JSON.parse(localStorage.getItem(LS_COMPANY_INFO_KEY) || '{}');
			return info?.dataFrequencyType || null;
		} catch {
			return null;
		}
	};

	const getFrequencyPeriodBadge = () => {
		if (!form.frequency || !form.period) return null;
		const freq = form.frequency;
		const freqLabel = FormFrequency[freq]?.value;
		if (!freqLabel) return null;

		const periodMoment = moment(form.period);
		const dataFrequencyType = getCompanyDataFrequencyType();
		let start, periodText;
		let end = periodMoment;
		const m = periodMoment.month(); // 0-indexed

		if (freq === 'MONTHLY') {
			periodText = end.format("MMM YYYY");
		} else if (freq === 'QUARTERLY') {
			start = moment(form.period).subtract(2, 'months');
			periodText = `${start.format("MMM YYYY")} to ${end.format("MMM YYYY")}`;
		} else if (freq === 'HALF_YEARLY') {
			if (dataFrequencyType === 'FY') {
				// FY halves: Apr–Sep and Oct–Mar
				if (m >= 3 && m <= 8) {
					start = moment({ year: periodMoment.year(), month: 3 }); // April same year
				} else if (m >= 0 && m <= 2) {
					start = moment({ year: periodMoment.year() - 1, month: 9 }); // October prev year
				} else {
					start = moment(form.period).subtract(5, 'months'); // fallback
				}
			} else if (dataFrequencyType === 'CY') {
				// CY halves: Jan–Jun and Jul–Dec
				start = m <= 5
					? moment({ year: periodMoment.year(), month: 0 }) // January same year
					: moment({ year: periodMoment.year(), month: 6 }); // July same year
			} else {
				start = moment(form.period).subtract(5, 'months');
			}
			periodText = `${start.format("MMM YYYY")} to ${end.format("MMM YYYY")}`;
		} else if (freq === 'ANNUALLY') {
			if (dataFrequencyType === 'FY') {
				// FY ends in March. Period is either the FY end month (Mar) or the first month of
				// the new FY (Apr), both meaning the same reported year. For May–Dec the FY ends next March.
				const fyEndYear = m <= 3 ? periodMoment.year() : periodMoment.year() + 1;
				start = moment({ year: fyEndYear - 1, month: 3 }); // April of prev year
				end = moment({ year: fyEndYear, month: 2 });         // March of fyEndYear
			} else if (dataFrequencyType === 'CY') {
				start = moment({ year: periodMoment.year(), month: 0 });
				end = moment({ year: periodMoment.year(), month: 11 });
			} else {
				start = moment(form.period).subtract(11, 'months');
			}
			periodText = `${start.format("MMM YYYY")} to ${end.format("MMM YYYY")}`;
		} else {
			return null;
		}

		return (
			<span
				className="badge rounded-pill"
				style={{ ...FREQUENCY_BADGE_STYLES[freq], fontSize: '0.78rem', fontWeight: 500, padding: '5px 12px' }}
			>
				{freqLabel} · {periodText}
			</span>
		);
	};

	const renderSharedWithDetails = () => {
		const days = form.remindBefore == 0 ? form.remindBefore + 1 : form.remindBefore;
		const lastDate = new Date(form.lastSubmissionDate);
		let totaldays = null;
		totaldays = days === null ? 1 : days;
		const tenDaysBefore = new Date(lastDate);
		tenDaysBefore.setDate(lastDate.getDate() - totaldays);
		const formattedDate = tenDaysBefore.toISOString().split('T')[0];
		return (
			<div className="Share_withCard 1">
				<span>{t('Permissions.label')}</span>
				<div className="mb-3">
					<ul>
						{
							selfAssessment
								?
								renderPermissionBubbles()
								:
								form.sharedWith?.map((s, sIndex) => {
									return (
										<li key={`assmtSharedGroup-${sIndex}`}>{s.name}</li>
									);
								})
						}
					</ul>
				</div>
				{
					form.reminderEnabled
						?
						<div><span>Occurs every {form.daysOffset} day(s) from  {moment(formattedDate).format("MMMM DD, YYYY")}</span></div>
						:
						<div><span>{t("NoReminder.label")}</span></div>
				}
			</div>
		);
	};

	const transformAssessmentName = () => {
		if (formType !== FormType.SURVEY) {
			return (
				<>
					{form.assessmentForm?.name} - {moment(form?.period).format("MMMM, YYYY")}
				</>
			);
		};
		const parts = form.assessmentForm?.name.split(" V");
		const baseName = parts[0].trim();
		const version = parts[1]?.split(" ")[0];

		return (
			<>
				{baseName}
				{version && <Badge pill bg='info' text='white' className='ms-2'>Version {version}</Badge>}
			</>
		);
	}

	const renderBody = () => {
		return (
			<div className="card-body pb-1">
				<div className="row m-0">
					<div className="col-lg-4 col-md-12 p-2">
						<div className="row m-0">
							<div className="col-lg-11 col-md-11 p-0">
								<div>
									<h5 className="cursor" onClick={() => openSubmissions()}>
										{transformAssessmentName()}
										{
											form.calendarEventRef > 0 && form.frequency &&
											<span className="mx-2 badge bg-badge-New rounded-pill">
												<MdLoop className="me-1" />{FormFrequency[form.frequency].value}
											</span>
										}
									</h5>
								</div>
								{/* <p>{form.assessmentForm?.description}</p> */}
								<div className="mt-2 mb-2">
									{getFrequencyPeriodBadge()}
								</div>
								<div className="from_data">
									<small>
										{form.assessmentForm?.name}
										<div>
											<ul>
												<li>{form.assessmentForm?.sectionCount ?? 0} {t('Sections.label')}</li>
												<li>{form.assessmentForm?.subSectionCount ?? 0} {t('SubSections.label')}</li>
												<li>{form.assessmentForm?.questionCount ?? 0} {t('Questions.label')}</li>
											</ul>
										</div>
									</small>
								</div>
							</div>
						</div>
					</div>
					<div className="col-lg-2 col-md-4 col-sm-4 p-2 Reporting_PeriodBox">
						<div className="mb-3">
							<span>{formType === FormType.SURVEY ? t('Year.label') : t('ReportingPeriod.label')}</span>
							<div>
								<b>
									{
										form.period
											? moment(form.period).format("MMM, YYYY")
											: ''
									}
								</b>
							</div>
						</div>
						<div>
							<span>{t('ExpiryDate.label')}</span>
							<div>
								<b>
									{form?.lastSubmissionDate ? moment(form?.lastSubmissionDate).format("MMM DD, YYYY") : ''}
								</b>
							</div>
						</div>
					</div>
					<div className="col-lg-2 col-md-4 col-sm-4 p-2">
						{renderSharedWithDetails()}
					</div>
					<div className="col-lg-4 col-md-12 col-sm-12 p-2">
						{renderSubmissionCounts()}
					</div>

					{/* <div className="Assessment_percentagebox">
						<CircularProgressbar
							value={percentage.toFixed(0)}
							text={`${percentage.toFixed(0)}%`}
							styles={buildStyles({
								pathColor: '#0d6efd',
								textColor: '#000',
								trailColor: '#dee8ed'
							})}
						/>
					</div> */}
				</div>
			</div>
		);
	};

	const renderFooter = () => {
		return (

			<div className="card-footer">
				<div className="AsessmentCard_footer">
					<div className="Assessment_Foot_in">
						{renderStatusBadges()}
						{
							!isExpired && (!isCompleted || notSharedCount > 0) &&
							<Button
								variant={`${form.sharedOn ? 'secondary' : 'primary'}`}
								hidden={!hasDataCollectionUpdatePermission()}
								className="px-4 ms-2"
								onClick={() => form.sharedOn ? onReshareClick() : onShareClick()}>
								{form.sharedOn ? t("Reshare.label") : t("Share.label")}
							</Button>
						}

						{form.sharedOn && (
							<Button variant="secondary"
								className="px-4 ms-2"
								onClick={() => openSubmissions('responses')} >
								{t("Responses.label")}
							</Button>
						)}

						{formType === FormType.SURVEY && form.sharedOn && (
							<Button variant="secondary"
								className="px-4 ms-2"
								onClick={() => navigate("../scorecard/list" + `?period=${form.period}&aId=${form.id}`)} >
								{t("ScoreCard.label")}
							</Button>
						)}

						{
							form.sharedOn && !isExpired && (!isCompleted || notSharedCount > 0) &&
							<Button variant="secondary"
								className="px-4 ms-2"
								hidden={!hasDataCollectionUpdatePermission()}
								onClick={() => onQuickRemindClick()}>
								{t('SendReminder2.label')}
							</Button>
						}

						{
							formType === FormType.GENERAL && hasDataCollectionViewPermission() &&
							<Button
								variant="secondary"
								className="px-4 ms-2"
								onClick={() => onShowQuestionPermissions?.()}
								data-testid="dce-open-question-permissions"
							>
								{t('DCEQuestionPermissions.openButton.label')}
							</Button>
						}
					</div>
					<div className="Assessment_Foot_in02">
						{
							form.assessmentForm?.status === 'DRAFT' &&
							<OverlayTrigger
								placement="auto"
								overlay={<Tooltip>{t('FormNotPublishedTooltip.label')}</Tooltip>} >
								<span className="badge bg-badge-Critical rounded-pill"><AiOutlineWarning /> {t('FormNotPublished.label')}</span>
							</OverlayTrigger>

						}
						<Button className="px-2" hidden={!hasDataCollectionUpdatePermission()}
							variant="blank_outline"
							onClick={() => onEditAssessment()}>
							<BiPencil />
						</Button>
						{
							(formType === FormType.SURVEY && isExpired) || (formType === FormType.SURVEY && !form.sharedOn) ?
								<></> :
								<Dropdown className="fw-bold" hidden={!hasDataCollectionUpdatePermission()}>
									<Dropdown.Toggle
										variant="blank_outline"
										className="px-2 h-100"
										id="dropdown-basic">
										<AiOutlineSetting />
									</Dropdown.Toggle>
									<Dropdown.Menu>
										{
											formType === FormType.GENERAL &&
											<>
												<Dropdown.Item onClick={() => onExpiryEdit()}>
													{t("EditExpiryDate.label")}
												</Dropdown.Item>
												<Dropdown.Item
													onClick={() => onDuplicate()}>
													{t('Duplicate.label')}
												</Dropdown.Item>
											</>
										}
										{
											form.sharedOn && !isExpired &&
											<Dropdown.Item
												onClick={() => onReminderSetup()}>
												{t('SetReminder.label')}
											</Dropdown.Item>
										}
									</Dropdown.Menu>
								</Dropdown>
						}
					</div>
				</div>
			</div>
		);
	};

	const openSubmissions = (targetTab) => {
		// Map internal tab keys to query param values expected by AssessmentSubmissionList
		let queryString = '';
		let activeTabState = undefined;

		if (targetTab === 'responses') {
			queryString = '?tab=res';
			activeTabState = 'responses';
		} else if (targetTab === 'approvals') {
			queryString = '?tab=app';
			activeTabState = 'approvals';
		}

		const navigationState = {
			assessmentName: `${form.assessmentForm?.name} - ${moment(form?.period).format("MMMM, YYYY")}`
		};

		if (activeTabState) {
			navigationState.activeTab = activeTabState;
		}

		if (formType === FormType.SURVEY) {
			navigate(`/company_admin/dmat/matassessments/${form.id}/submissions/${selfAssessment}${queryString}`, {
				state: navigationState,
			});
		} else {
			navigate(`/company_admin/assessments/${form.id}/submissions/${selfAssessment}${queryString}`, {
				state: navigationState,
			});
		}
	};

	const handleStatusClick = (e, statusType) => {
		// Status-based navigation to Responses tab is only applicable for general data collection events
		if (formType !== FormType.GENERAL) {
			openSubmissions();
			return;
		}

		e?.preventDefault();
		e?.stopPropagation();

		// Map status types to filter keys
		let childStatusFilters = {};
		let selectedStatuses = [];

		switch (statusType) {
			case 'COMPLETED':
				childStatusFilters = { COMPLETED: true };
				selectedStatuses = ['COMPLETED'];
				break;
			case 'IN_PROGRESS':
				childStatusFilters = { IN_PROGRESS: true };
				selectedStatuses = ['IN_PROGRESS'];
				break;
			case 'DRAFT':
				childStatusFilters = { DRAFT: true };
				selectedStatuses = ['DRAFT'];
				break;
			case 'NOT_SHARED':
				childStatusFilters = { NOT_SHARED: true };
				selectedStatuses = ['NOT_SHARED'];
				break;
			case 'PENDING_APPROVAL':
				// Set all 3 levels for pending approval
				childStatusFilters = {
					PENDING_APPROVAL_LEVEL_1: true,
					PENDING_APPROVAL_LEVEL_2: true,
					PENDING_APPROVAL_LEVEL_3: true
				};
				selectedStatuses = ['PENDING_APPROVAL_LEVEL_1', 'PENDING_APPROVAL_LEVEL_2', 'PENDING_APPROVAL_LEVEL_3'];
				break;
			case 'CORRECTION_PENDING':
				childStatusFilters = { CORRECTION_PENDING: true };
				selectedStatuses = ['CORRECTION_PENDING'];
				break;
			default:
				childStatusFilters = {};
				selectedStatuses = [];
		}

		// Convert selected statuses to comma-separated string for API
		const childStatus = selectedStatuses.length > 0 ? selectedStatuses.join(",") : null;

		// Determine tab and filter key based on status type
		const isPendingApproval = statusType === 'PENDING_APPROVAL';
		const tabKey = isPendingApproval ? 'approvals' : 'responses';
		const tabParam = isPendingApproval ? 'app' : 'res';
		const activeTabName = isPendingApproval ? 'approvals' : 'responses';

		// Clear any previous filters and save new filter to localStorage
		const filterData = {
			parentStatusFilters: {},
			childStatusFilters: childStatusFilters,
			parentStatus: null,
			childStatus: childStatus,
			submittedDate: null,
			_timestamp: Date.now() // Add timestamp to force refresh
		};

		// Remove any previously saved filter and save new one
		const savedData = localStorage.getItem(LS_DCE_SUBMISSION_FILTER_KEY);
		const allFilters = savedData ? JSON.parse(savedData) : {};
		// Clear previous filter and set new one
		allFilters[tabKey] = filterData;
		localStorage.setItem(LS_DCE_SUBMISSION_FILTER_KEY, JSON.stringify(allFilters));

		// Navigate to submissions page with appropriate tab
		navigate(`/company_admin/assessments/${form.id}/submissions/${selfAssessment}?tab=${tabParam}`, {
			state: {
				assessmentName: `${form.assessmentForm?.name} - ${moment(form?.period).format("MMMM, YYYY")}`,
				activeTab: activeTabName,
				forceFilterRefresh: true
			},
		});
	};

	const renderStatusBadges = () => {
		
		if (showCompletedBadge) {
			return (
				<span className="badge bg-badge-Finalized rounded-pill">{t("Completed.label")}</span>
			);
		} else if (isExpired) {
			return (
				<span className="badge bg-badge-Inactive rounded-pill ms-1">{t("Expired.label")}</span>
			);
		} else if (showInProgressBadge) {
			return (
				<span className="badge bg-badge-Inprogress rounded-pill">{t("InProgress.label")}</span>
			);
		} else if (showNotStartedBadge) {
			return (
				<span className="badge bg-badge-New rounded-pill">{t("NotStarted.label")}</span>
			);
		}
		return <></>;
	};

	const renderSubmissionCountLabel = (labelName, labelClass, labelCount) => {
		if (viewMode === 'LIST_CARD') {
			return (
				<>
					<BsFillCircleFill className={labelClass} />
					<div className="Form_status1">{labelName}</div>
					<b>{labelCount}</b>
				</>
			);
		} else {
			return <><span><BsFillSquareFill className={labelClass} /> {labelName}</span> {labelCount}</>;
		}
	};

	const renderSubmissionCounts = () => {
		const submissionStatusCount = form.submissionStatusCount || {};
		const completedCount = submissionStatusCount.COMPLETED ?? 0;
		const inProgressDirectCount = submissionStatusCount.IN_PROGRESS ?? 0;
		const notStartedCount = submissionStatusCount.DRAFT ?? 0;
		const notSharedCount = submissionStatusCount.NOT_SHARED ?? 0;
		const pendingApprovalCount = submissionStatusCount.PENDING_APPROVAL ?? 0;
		const correctionPendingCount = submissionStatusCount.CORRECTION_PENDING ?? 0;

		// Calculate total count
		const totalCount = completedCount +
			inProgressDirectCount +
			notStartedCount +
			correctionPendingCount +
			pendingApprovalCount;

		return (
			<div className="Form_SubmitDts">

				<div className="status-columns">
					<ul className="status-column-left">
						<li>
							{viewMode === 'LIST_CARD' ? (
								<>
									<BsCircle className="text-secondary" />
									<div className="Form_status1"><b>{t("Total.label")}</b></div>
									<b>{totalCount}</b>
								</>
							) : (
								<><span><BsCircle className="text-secondary" /> <b>{t("Total.label")}</b></span> <b>{totalCount}</b></>
							)}
						</li>
						<li
							className={formType === FormType.GENERAL && completedCount > 0 ? "status-link" : ""}
							onClick={(e) => formType === FormType.GENERAL && completedCount > 0 && handleStatusClick(e, 'COMPLETED')}
							style={formType === FormType.GENERAL && completedCount > 0 ? { cursor: 'pointer' } : {}}
						>
							{renderSubmissionCountLabel(t("Completed.label"), 'text-Medium', completedCount)}
						</li>
						<li
							className={formType === FormType.GENERAL && inProgressDirectCount > 0 ? "status-link" : ""}
							onClick={(e) => formType === FormType.GENERAL && inProgressDirectCount > 0 && handleStatusClick(e, 'IN_PROGRESS')}
							style={formType === FormType.GENERAL && inProgressDirectCount > 0 ? { cursor: 'pointer' } : {}}
						>
							{renderSubmissionCountLabel(t("InProgress.label"), 'text-High', inProgressDirectCount)}
						</li>
						<li
							className={formType === FormType.GENERAL && notStartedCount > 0 ? "status-link" : ""}
							onClick={(e) => formType === FormType.GENERAL && notStartedCount > 0 && handleStatusClick(e, 'DRAFT')}
							style={formType === FormType.GENERAL && notStartedCount > 0 ? { cursor: 'pointer' } : {}}
						>
							{renderSubmissionCountLabel(t("Notstarted.label"), 'text-Low', notStartedCount)}
						</li>
					</ul>
					<ul className="status-column-right">
						{/* <li
							className={formType === FormType.GENERAL && notSharedCount > 0 ? "status-link" : ""}
							onClick={(e) => formType === FormType.GENERAL && notSharedCount > 0 && handleStatusClick(e, 'NOT_SHARED')}
							style={formType === FormType.GENERAL && notSharedCount > 0 ? { cursor: 'pointer' } : {}}
						>
							{renderSubmissionCountLabel(t("NotShared.label"), 'text-secondary', notSharedCount)}
						</li> */}
						{correctionPendingCount > 0 && (
							<li
								className={formType === FormType.GENERAL ? "status-link" : ""}
								onClick={(e) => formType === FormType.GENERAL && handleStatusClick(e, 'CORRECTION_PENDING')}
								style={formType === FormType.GENERAL ? { cursor: 'pointer' } : {}}
							>
								{renderSubmissionCountLabel(t("CorrectionPending.label"), 'text-danger', correctionPendingCount)}
							</li>
						)}
						{pendingApprovalCount > 0 && (
							<li
								className={formType === FormType.GENERAL ? "status-link" : ""}
								onClick={(e) => formType === FormType.GENERAL && handleStatusClick(e, 'PENDING_APPROVAL')}
								style={formType === FormType.GENERAL ? { cursor: 'pointer' } : {}}
							>
								{renderSubmissionCountLabel(t("PendingApproval.label"), 'text-Accepted', pendingApprovalCount)}
							</li>
						)}
					</ul>
				</div>
			</div>
		);
	};

	const renderOverviewCard = () => {
		return (
			<div className='SummaryIssueCard 1'>
				<div className='SummaryIssueCard_head'>
					<div className='SummaryIssueCard_head_in'>
						<h5>Materiality Survey - {moment(form?.period).format("YYYY")}</h5>
						{renderStatusBadges()}
					</div>
					<div className="mt-2">
						<p className="mb-0">
							<span >{t('ExpiresOn.label')}</span>
							<span>{form?.lastSubmissionDate ? moment(form?.lastSubmissionDate).format("MMM DD, YYYY") : ''}</span>
						</p>
					</div>
				</div>
				<div className='SummaryIssueCard_body'>
					<div className='SummaryIssueCard_body_in 2'>
						<div className='SummaryIssueCard_Prograss'>
							<StackedCircularProgressBar
								segments={[
									{ value: form.submissionStatusCount?.COMPLETED ?? 0, color: '#51b133' },
									{ value: inProgressCount ?? 0, color: '#f99c35' },
									{ value: form.submissionStatusCount?.DRAFT ?? 0, color: '#dc3545' },
									{ value: form.submissionStatusCount?.NOT_SHARED ?? 0, color: '#0063b8' },
								]}
								total={totalFieldsCount}
								displayTextToOverride={`${percentage.toFixed(0)}%`}
							/>
						</div>
						<div className=" ">
							{renderSubmissionCounts()}
						</div>
					</div>
				</div>
				<div className='SummaryIssueCard_footer'>
					{t("ForMoreDetails.label")} <a href="#" onClick={(e) => { e.preventDefault(); navigate("../dmat/matassessments"); }}>{t("GoToSurveys.label")}</a>
				</div>
			</div>
		)
	};

	return (
		<>
			{
				viewMode === 'LIST_CARD'
					?
					<div className="card mb-2">
						{renderBody()}
						{renderFooter()}
					</div>
					:
					viewMode === 'OVERVIEW_CARD' ? renderOverviewCard() : <></>
			}
		</>
	);
}

export default withTranslation()(AssessmentListCard);
